import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, functions } from '../services/firebase';
import { useAuth } from './useAuth';
import { DEFAULT_BRANDING } from '../constants/defaultBranding';
import type { SchoolBranding } from '@shared/types';

interface UpdateBrandingRequest {
  schoolId: string;
  fileType: 'logo' | 'emailHeader' | 'pdfHeader';
  fileData?: string;
  fileName?: string;
  primaryColor?: string;
  secondaryColor?: string;
  action: 'upload' | 'updateColors' | 'delete';
}

export function useBranding() {
  const { customClaims } = useAuth();
  const schoolId = customClaims?.schoolId || 'school_BAU_vienna_2025';
  const queryClient = useQueryClient();

  // Fetch current branding and school data
  const { data, isLoading } = useQuery({
    queryKey: ['branding', schoolId],
    queryFn: async () => {
      if (!schoolId) return null;
      // Read the PUBLIC projection (branding + name), not the auth-gated
      // schools/{id} doc — this hook runs on pre-auth pages. [C2 rules audit]
      const schoolDoc = await getDoc(doc(db, 'schoolPublic', schoolId));
      const schoolData = schoolDoc.data();
      // Handle both old (string) and new (object with bg/de/en) format
      const nameValue = schoolData?.name;
      const schoolName = typeof nameValue === 'string' ? nameValue : nameValue?.bg;
      const schoolNameLocalized = typeof nameValue === 'object' ? nameValue as Record<string, string> : undefined;
      return {
        branding: schoolData?.branding as SchoolBranding | undefined,
        schoolName: schoolName as string | undefined,
        schoolNameLocalized,
      };
    },
    enabled: !!schoolId,
  });

  // Fallback to bundled DEFAULT_BRANDING докато Firestore doc не зареди —
  // елиминира multi-second flicker от generic blue към school colors.
  // Когато data пристигне и се различава от defaults → smooth re-render.
  const branding = data?.branding ?? DEFAULT_BRANDING.branding;
  const schoolName = data?.schoolName ?? DEFAULT_BRANDING.schoolName;
  const schoolNameLocalized =
    data?.schoolNameLocalized ?? DEFAULT_BRANDING.schoolNameLocalized;

  // Update branding mutation
  const updateBranding = useMutation({
    mutationFn: async (request: Omit<UpdateBrandingRequest, 'schoolId'>) => {
      const fn = httpsCallable(functions, 'updateBrandingHttp');
      const result = await fn({ ...request, schoolId });
      return result.data as { success: boolean; fileUrl?: string; message: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branding', schoolId] });
    },
  });

  // Helper: Upload logo
  const uploadLogo = async (file: File) => {
    const base64 = await fileToBase64(file);
    return updateBranding.mutateAsync({
      fileType: 'logo',
      fileData: base64,
      fileName: file.name,
      action: 'upload',
    });
  };

  // Helper: Delete logo
  const deleteLogo = async () => {
    return updateBranding.mutateAsync({
      fileType: 'logo',
      action: 'delete',
    });
  };

  // Helper: Update colors
  const updateColors = async (primaryColor: string, secondaryColor: string) => {
    return updateBranding.mutateAsync({
      fileType: 'logo', // Not used for colors
      primaryColor,
      secondaryColor,
      action: 'updateColors',
    });
  };

  // Update school name mutation
  const updateSchoolNameMutation = useMutation({
    mutationFn: async (name: string) => {
      if (!schoolId) throw new Error('No school ID');
      // Save as multilingual object to match backend expectations (name.bg)
      await updateDoc(doc(db, 'schools', schoolId), {
        name: { bg: name },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branding', schoolId] });
    },
  });

  // Helper: Update school name
  const updateSchoolName = async (name: string) => {
    return updateSchoolNameMutation.mutateAsync(name);
  };

  return {
    branding,
    schoolName,
    schoolNameLocalized,
    isLoading,
    uploadLogo,
    deleteLogo,
    updateColors,
    updateSchoolName,
    isUpdating: updateBranding.isPending || updateSchoolNameMutation.isPending,
    error: updateBranding.error || updateSchoolNameMutation.error,
  };
}

// Helper function
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
